import { db } from "../lib/firebase";
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, Timestamp } from "firebase/firestore";

export interface CustomDomainRequest {
  id?: string;
  userId: string;
  userEmail: string;
  userName: string;
  customDomain: string;
  subdomain: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  createdAt: Date;
  updatedAt: Date;
  adminNotes?: string;
  rejectionReason?: string;
}

/**
 * สร้างคำขอ Custom Domain ใหม่
 */
export const createCustomDomainRequest = async (
  userId: string,
  userEmail: string,
  userName: string,
  customDomain: string,
  subdomain: string
): Promise<string> => {
  try {
    const requestData = {
      userId,
      userEmail,
      userName,
      customDomain,
      subdomain,
      status: 'pending',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, "customDomainRequests"), requestData);
    console.log('[CustomDomainService] Request created:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('[CustomDomainService] Error creating request:', error);
    throw error;
  }
};

/**
 * ดึงคำขอทั้งหมด (สำหรับ Admin)
 */
export const getAllCustomDomainRequests = async (): Promise<CustomDomainRequest[]> => {
  try {
    const q = query(
      collection(db, "customDomainRequests"),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const requests: CustomDomainRequest[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      requests.push({
        id: doc.id,
        userId: data.userId,
        userEmail: data.userEmail,
        userName: data.userName,
        customDomain: data.customDomain,
        subdomain: data.subdomain,
        status: data.status,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        adminNotes: data.adminNotes,
        rejectionReason: data.rejectionReason,
      });
    });
    
    return requests;
  } catch (error) {
    console.error('[CustomDomainService] Error fetching requests:', error);
    throw error;
  }
};

/**
 * ดึงคำขอของผู้ใช้คนหนึ่ง
 */
export const getUserCustomDomainRequests = async (userId: string): Promise<CustomDomainRequest[]> => {
  try {
    const q = query(
      collection(db, "customDomainRequests"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const requests: CustomDomainRequest[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      requests.push({
        id: doc.id,
        userId: data.userId,
        userEmail: data.userEmail,
        userName: data.userName,
        customDomain: data.customDomain,
        subdomain: data.subdomain,
        status: data.status,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        adminNotes: data.adminNotes,
        rejectionReason: data.rejectionReason,
      });
    });
    
    return requests;
  } catch (error) {
    console.error('[CustomDomainService] Error fetching user requests:', error);
    throw error;
  }
};

/**
 * อัปเดตสถานะคำขอ (สำหรับ Admin)
 */
export const updateCustomDomainRequestStatus = async (
  requestId: string,
  status: 'pending' | 'processing' | 'completed' | 'rejected',
  adminNotes?: string,
  rejectionReason?: string
): Promise<void> => {
  try {
    const requestRef = doc(db, "customDomainRequests", requestId);
    
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };
    
    if (adminNotes) {
      updateData.adminNotes = adminNotes;
    }
    
    if (rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }
    
    await updateDoc(requestRef, updateData);
    console.log('[CustomDomainService] Request updated:', requestId);
  } catch (error) {
    console.error('[CustomDomainService] Error updating request:', error);
    throw error;
  }
};

/**
 * ลบคำขอ Custom Domain (สำหรับ Admin)
 */
export const deleteCustomDomainRequest = async (requestId: string): Promise<void> => {
  try {
    const requestRef = doc(db, "customDomainRequests", requestId);
    await deleteDoc(requestRef);
    console.log('[CustomDomainService] Request deleted:', requestId);
  } catch (error) {
    console.error('[CustomDomainService] Error deleting request:', error);
    throw error;
  }
};
